/**
 * Minimal client for api-next's account mutations at same-origin /v2/graphql.
 * Not Apollo: these pages only send mutations from the browser, and the session
 * lives in httpOnly cookies api-next sets on path /v2, so there is nothing to
 * cache or render on the server.
 */
const ENDPOINT = '/v2/graphql';

export class ApiError extends Error {
    code?: string;

    field?: string;

    /** RATE_LIMITED: seconds until another try is allowed, when the API says. */
    retryAfterSeconds?: number;

    constructor(
        message: string,
        code?: string,
        field?: string,
        retryAfterSeconds?: number,
    ) {
        super(message);
        this.code = code;
        this.field = field;
        this.retryAfterSeconds = retryAfterSeconds;
    }
}

/** api-next's CSRF cookie is readable on purpose; mutations echo it in a header. */
function csrfToken(): string | undefined {
    return document.cookie
        .split('; ')
        .find(c => c.startsWith('nearbites_csrf='))
        ?.slice('nearbites_csrf='.length);
}

interface GraphQLBody<T> {
    data?: T | null;
    errors?: {
        message: string;
        extensions?: {
            code?: string;
            field?: string;
            retryAfterSeconds?: number;
        };
    }[];
}

async function post(
    query: string,
    variables: Record<string, unknown>,
): Promise<Response> {
    const token = csrfToken();
    return fetch(ENDPOINT, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'content-type': 'application/json',
            ...(token ? { 'x-csrf-token': token } : {}),
        },
        body: JSON.stringify({ query, variables }),
    });
}

export async function v2<T>(
    query: string,
    variables: Record<string, unknown> = {},
): Promise<T> {
    let res = await post(query, variables);
    // A session cookie with no CSRF cookie yet (first request in a while): the
    // 403 response sets one, so try once more with it.
    if (res.status === 403) res = await post(query, variables);

    const body: GraphQLBody<T> | null = await res.json().catch(() => null);
    const error = body?.errors?.[0];
    if (error) {
        throw new ApiError(
            error.message,
            error.extensions?.code ??
                (res.status === 429 ? 'RATE_LIMITED' : undefined),
            error.extensions?.field,
            error.extensions?.retryAfterSeconds,
        );
    }
    if (!res.ok || !body?.data) {
        throw new ApiError('Something went wrong. Please try again.');
    }
    return body.data;
}

/** Message for an error thrown by v2(), safe to show as-is. */
export function messageOf(err: unknown): string {
    if (err instanceof ApiError) {
        if (err.code === 'FEATURE_DISABLED') {
            return 'Accounts are not available yet.';
        }
        return err.message;
    }
    return 'We could not reach NearBites. Check your connection and try again.';
}

/** Where to go after signing in: a same-site path from ?next=, else home. */
export function safeNext(search: string): string {
    const next = new URLSearchParams(search).get('next');
    if (
        next &&
        next.startsWith('/') &&
        !next.startsWith('//') &&
        !next.startsWith('/\\')
    ) {
        return next;
    }
    return '/';
}

export const REGISTER = `mutation Register($input: RegisterInput!) { register(input: $input) }`;
export const LOGIN = `mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) { account { id } }
}`;
export const GOOGLE = `mutation SignInWithGoogle($idToken: String!) {
    signInWithGoogle(idToken: $idToken) { account { id } }
}`;
export const VERIFY = `mutation VerifyEmail($email: String!, $code: String!) {
    verifyEmail(email: $email, code: $code) { account { id } }
}`;
export const RESEND = `mutation Resend($email: String!) { resendVerificationCode(email: $email) }`;
export const FORGOT = `mutation Forgot($email: String!) { requestPasswordReset(email: $email) }`;
export const RESET = `mutation Reset($token: String!, $newPassword: String!) {
    resetPassword(token: $token, newPassword: $newPassword)
}`;

/** What to call someone: their display name, or their username until they set one. */
export const nameOf = (viewer: {
    displayName?: string | null;
    username: string;
}) => viewer.displayName?.trim() || viewer.username;

export const VIEWER = `query Viewer { viewer { id email username displayName avatarUrl } }`;
export const REFRESH = `mutation Refresh { refreshSession { account { id } } }`;
export const LOGOUT = `mutation Logout { logout }`;

/** api-next's JS-readable "probably signed in" flag (not a credential). */
export const SIGNED_IN_COOKIE = 'nearbites_signed_in';

export function hasSignedInHint(): boolean {
    return document.cookie.split('; ').some(c => c === `${SIGNED_IN_COOKIE}=1`);
}

export function clearSignedInHint(): void {
    document.cookie = `${SIGNED_IN_COOKIE}=; Max-Age=0; path=/`;
}

/**
 * Fired on window when someone signs in without a page load (the sign-in
 * dialog), so HeaderAccount can pick up the new session.
 */
export const SIGNED_IN_EVENT = 'nearbites:signed-in';

export function announceSignedIn(): void {
    window.dispatchEvent(new Event(SIGNED_IN_EVENT));
}

export const SETTINGS_VIEWER = `query SettingsViewer {
    viewer {
        id email username displayName avatarUrl bio hasPassword googleLinked
        dietaryPreferences { slug name }
    }
    dietaryOptions { slug name }
}`;
export const UPDATE_PROFILE = `mutation UpdateProfile($input: ProfileInput!) {
    updateProfile(input: $input) { displayName username bio }
}`;
export const SET_DIETARY = `mutation SetDietary($slugs: [String!]!) {
    setDietaryPreferences(slugs: $slugs) { slug }
}`;
export const SET_AVATAR = `mutation SetAvatar($image: String!) { setAvatar(image: $image) { avatarUrl } }`;
export const REMOVE_AVATAR = `mutation RemoveAvatar { removeAvatar { avatarUrl } }`;
export const CHANGE_PASSWORD = `mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
    changePassword(currentPassword: $currentPassword, newPassword: $newPassword)
}`;
export const LOGOUT_EVERYWHERE = `mutation LogoutEverywhere { logoutEverywhere }`;
export const DELETE_ACCOUNT = `mutation DeleteAccount($password: String) { deleteAccount(password: $password) }`;
